// apps/client/src/ui/screens/foundTown.tsx (M0-T17) — name, hemisphere, one of four seeded layouts, the founder's plot, device time zone
import { tuning } from '@whiskerton/content';
import { isValidTimeZone, type GeneratedTown, type Hemisphere } from '@whiskerton/shared';
import { generateTown } from '@whiskerton/shared/map/generate';   // subpath-only (not in the barrel)
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/endpoints';
import { HttpError } from '../../api/http';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel, Tabs, TextField } from '../kit';
import { freshSeeds, Minimap } from '../minimap';
import { defaultPlot, pickablePlots } from '../plotPick';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { foundTown: undefined } }

const LAYOUTS = 4;
const MAP_ASPECT = tuning.map.w / tuning.map.h;
/** One generation per animation frame, so the screen stays responsive while four layouts build. */
const nextFrame = (fn: () => void): (() => void) => {
  if (typeof requestAnimationFrame === 'function') { const id = requestAnimationFrame(fn); return () => cancelAnimationFrame(id); }
  const id = setTimeout(fn, 0); return () => clearTimeout(id);
};
function deviceTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : 'UTC';
  } catch { return 'UTC'; }
}

// Layout tweaks for kit Buttons on this screen, through wrapper classes so the kit's Button keeps its own API: a compact Reroll, and a
// Found button whose long label (it carries the town name) wraps onto a second line instead of widening the row or being cut short
// (M1 playtest: "Found Pebbl…" at 844 × 390); both action buttons are a little more compact than a standard button.
//
// Landscape phone (§8.7): name, hemisphere and the four layouts on the left; the plot map on the right with the line saying what Found
// still needs and Back/Found under it, so all four layouts and the Found button show without a scroll (the hint and time zone may fall
// below; on a short, wide screen the layouts may too, but Found and the map never do). The right column is as tall as the panel allows (86cqh is the panel's max height, .panel; 52px
// its padding), and the map takes whatever height its column has left (a size container, so the map is as big as the room allows
// whatever the Found label wraps to). On a short, wide screen (844 × 300: Safari's bars and a short phone) the map instead fills the
// column's whole height, with the line and the actions stacked beside it; on a short, narrower one (750 × 340, 667 × 320) the "Pick your
// plot" heading gives its row to the map.
const PANEL_H = 'calc(86cqh - 52px)';
const PLOT_COL = 'clamp(200px, 32cqw, 460px)';
const CSS = '.found-reroll .btn { padding: 8px 14px 9px; font-size: 15px; }'
  + ' .found-actions .btn { padding: 11px 16px 12px; font-size: 16px; line-height: 1.15; }'
  + ' .found-actions .btn:last-child { min-width: 0; overflow-wrap: anywhere; text-wrap: balance; }'
  + ` .found-grid { display: grid; gap: 22px; width: 900px; max-width: 100%; align-items: stretch;
      grid-template-columns: minmax(0, 1fr) minmax(0, ${PLOT_COL}); }`   /* cqw: the panel is sized to fit its content, so a % would not resolve */
  + ` .found-plot { display: grid; gap: 8px; min-width: 0; grid-template-rows: auto minmax(0, 1fr) auto auto;
      grid-template-areas: "head" "map" "status" "actions"; max-height: ${PANEL_H};
      min-height: min(${PANEL_H}, calc(${PLOT_COL} / ${MAP_ASPECT} + 120px)); }`   /* room for the map at its column's full width */
  + ' .found-grid .field, .found-grid .field input { min-width: 0; }'
  + ' .found-plot-head { grid-area: head; }'
  + ' .found-map { grid-area: map; min-height: 0; container-type: size; display: grid; justify-items: center; align-items: start; }'
  + ` .found-map > * { width: min(100cqw, calc(100cqh * ${MAP_ASPECT})); }`
  + ' .found-status { grid-area: status; margin: 0; font-size: 12.5px; line-height: 1.25; }'
  + ' .found-actions { grid-area: actions; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 8px; }'
  + ` @container (max-height: 360px) and (max-aspect-ratio: 5 / 2) { .found-plot-head { display: none; }
      .found-plot { grid-template-rows: minmax(0, 1fr) auto auto; grid-template-areas: "map" "status" "actions"; } }`   /* the line under the map names the plot */
  + ` @container (max-height: 360px) and (min-aspect-ratio: 5 / 2) {
      .found-grid { grid-template-columns: minmax(0, 1fr) minmax(0, calc(${PANEL_H} * ${MAP_ASPECT} + 138px)); }
      .found-plot { grid-template-columns: minmax(0, 1fr) 130px; grid-template-rows: auto auto minmax(0, 1fr) auto;
        grid-template-areas: "map head" "map status" "map ." "map actions"; column-gap: 8px; }
      .found-actions { grid-template-columns: minmax(0, 1fr); }
      .found-actions .btn:last-child { order: -1; } }`;

function FoundTown({ close }: { close: () => void }) {
  const t = useT();
  const [name, setName] = useState('');
  const [hemisphere, setHemisphere] = useState<Hemisphere>('north');
  const [seeds, setSeeds] = useState<number[]>(() => freshSeeds(LAYOUTS));
  const [towns, setTowns] = useState<(GeneratedTown | null)[]>(() => Array<null>(LAYOUTS).fill(null));
  const [layout, setLayout] = useState(0);
  const [picked, setPicked] = useState<{ town: GeneratedTown; plot: number } | null>(null);   // the player's own pick, on that layout
  const [busy, setBusy] = useState(false);
  const timeZone = useMemo(deviceTimeZone, []);

  useEffect(() => {
    setTowns(Array<null>(LAYOUTS).fill(null));
    let cancel = () => {};
    const step = (i: number) => {
      if (i >= seeds.length) return;
      cancel = nextFrame(() => {
        const town = generateTown(seeds[i]!);
        setTowns((prev) => prev.map((x, j) => (j === i ? town : x)));
        step(i + 1);
      });
    };
    step(0);
    return () => cancel();
  }, [seeds]);

  const clean = name.replace(/\s+/g, ' ').trim();
  const nameOk = clean.length >= tuning.town.nameMinChars && clean.length <= tuning.town.townNameMaxChars;
  const chosen = towns[layout] ?? null;
  // A plot is picked for you (the free plot nearest the town square) as soon as the layout is drawn, so a town name is all Found needs;
  // tapping the map changes it, and a new layout starts again from its own default.
  const plot = !chosen ? null : picked?.town === chosen ? picked.plot : defaultPlot(chosen.map, pickablePlots(chosen.map.plots));
  const canFound = nameOk && plot !== null && chosen !== null && !busy;
  const status = !chosen ? t('towns.needLayout')
    : !nameOk ? t('towns.needName', { min: tuning.town.nameMinChars, max: tuning.town.townNameMaxChars })
    : plot === null ? t('towns.needPlot') : t('towns.plotPicked', { n: plot + 1 });

  const found = async () => {
    if (!canFound || plot === null) return;
    setBusy(true);
    try {
      const res = await api.foundTown({ name: clean, hemisphere, timeZone, seed: seeds[layout]!, plot });
      const s = useGame.getState();
      try { s.setMe(await api.me()); } catch { /* the list refreshes next boot; entering does not need it */ }
      s.closeAll();
      s.requestEnterTown(res.town.id);
    } catch (e) {
      useGame.getState().toast(e instanceof HttpError ? (`errors.${e.error}` as const) : 'errors.server_error', undefined, 'warn');
    } finally { setBusy(false); }
  };

  return (
    <div className="layer-full hit" style={{ display: 'grid', placeItems: 'center' }}>
      <Panel title={t('towns.foundTitle')}>
        <style>{CSS}</style>
        <div className="found-grid">
          <div style={{ display: 'grid', gap: 12, minWidth: 0, alignContent: 'start' }}>
            <TextField label={t('towns.nameLabel')} value={name} onChange={setName} maxLength={tuning.town.townNameMaxChars}
              placeholder={t('towns.nameHint', { min: tuning.town.nameMinChars, max: tuning.town.townNameMaxChars })} />
            <div style={{ display: 'flex', gap: '6px 12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong>{t('towns.hemisphere')}</strong>
              <Tabs tabs={[{ id: 'north', label: t('towns.north') }, { id: 'south', label: t('towns.south') }]} active={hemisphere}
                onChange={(id) => setHemisphere(id as Hemisphere)} />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', gap: '6px 12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <strong>{t('towns.pickLayout')}</strong>
                <span className="found-reroll">
                  <Button variant="quiet" disabled={busy}
                    onClick={() => { setSeeds(freshSeeds(LAYOUTS)); setLayout(0); setPicked(null); }}>{t('towns.reroll')}</Button>
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>   {/* one row: all four fit a phone in landscape */}
                {towns.map((town, i) => (
                  <button key={`${seeds[i]}-${i}`} type="button" className="hit" aria-label={t('towns.layout', { n: i + 1 })} aria-pressed={layout === i}
                    disabled={!town} onClick={() => { setLayout(i); setPicked(null); }}
                    style={{ border: 0, padding: 3, borderRadius: 12, minWidth: 0, background: layout === i ? 'var(--leaf-700)' : 'var(--cream-100)', cursor: 'pointer', minHeight: 48 }}>
                    {town ? <Minimap town={town} /> : <span>{t('common.loading')}</span>}
                  </button>
                ))}
              </div>
            </div>
            <small>{t('towns.hemisphereHint')} {t('towns.timeZone', { tz: timeZone })}</small>
          </div>
          <div className="found-plot">
            <strong className="found-plot-head">{t('towns.pickPlot')}</strong>
            <div className="found-map">
              {chosen ? <Minimap town={chosen} selected={plot} onPick={(n) => setPicked({ town: chosen, plot: n })} />
                : <p style={{ margin: 0 }}>{t('common.loading')}</p>}
            </div>
            <p className="found-status" data-testid="found-status" aria-live="polite">{status}</p>
            <div className="found-actions">
              <Button variant="quiet" disabled={busy} onClick={close}>{t('common.back')}</Button>
              <Button disabled={!canFound} onClick={() => void found()}>{busy ? t('towns.founding') : t('towns.foundButton', { name: clean })}</Button>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}
export const screen: ScreenDef<'foundTown'> = { id: 'foundTown', layer: 'full', component: FoundTown };
